import { useState } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogScrollArea,
} from '@/components/ui/dialog';
import { 
  CheckCircle, 
  Copy, 
  Info,
  Settings
} from 'lucide-react';
import { getMcpBaseUrl, getMcpConnectorUrl } from '@/config/mcpBaseUrl';

export interface ClaudeDesktopSuccessIntegration {
  integration_name: string;
  binding_id: string;
  auth_config: {
    api_key: string;
  };
  permissions: {
    can_read: boolean;
    can_write: boolean;
    can_search: boolean;
    can_delete: boolean;
  };
}

export interface ClaudeDesktopSuccessModalContentProps {
  onClose: () => void;
  integration: ClaudeDesktopSuccessIntegration;
  spaceName: string;
}

export function ClaudeDesktopSuccessModalContent({
  onClose,
  integration,
  spaceName,
}: ClaudeDesktopSuccessModalContentProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Per-space connector URL (OAuth): unique URL per integration for Claude Connectors
  const serverUrl = getMcpConnectorUrl(integration.binding_id);
  const bareBindingMcpUrl = `${getMcpBaseUrl()}/i/${integration.binding_id}/mcp`;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span>Claude Integration Ready</span>
          </DialogTitle>
          <DialogDescription>
            Connect Claude (Desktop or claude.ai) to your <strong>{spaceName}</strong> memory space by adding the <strong>{integration.integration_name}</strong> integration via Connectors using the URL below.
          </DialogDescription>
        </DialogHeader>

        <DialogScrollArea className="space-y-6 px-6">
          {/* MCP Server URL */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-blue-900">Memara MCP Server URL</h4>
                <p className="text-blue-700 text-sm">
                  Copy this URL for Settings → Connectors → Add custom connector (one connector per memory space).
                </p>
              </div>
              <Button
                onClick={() => handleCopy(serverUrl)}
                variant="outline"
                size="sm"
                className="text-blue-700 border-blue-300"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy URL'}
              </Button>
            </div>
            <div className="bg-white border border-blue-200 rounded p-3 font-mono text-sm break-all">
              {serverUrl}
            </div>
            <p className="text-blue-800 text-xs mt-3">
              For Bare MCP (API key over HTTP only), use:{' '}
              <span className="font-mono break-all">{bareBindingMcpUrl}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-2 h-7 text-blue-800"
                onClick={() => handleCopy(bareBindingMcpUrl)}
              >
                Copy
              </Button>
            </p>
          </div>

          {/* Connector setup steps */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-3 flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              Setup Steps
            </h4>
            <ol className="space-y-3 text-green-800 text-sm">
              <li className="flex items-start">
                <span className="bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-3 mt-0.5 font-medium">1</span>
                <div>
                  <strong>Open Connectors:</strong> in <strong>Claude Desktop</strong> use Settings → Connectors; on <strong>claude.ai</strong> use Settings → Connectors (web).
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-3 mt-0.5 font-medium">2</span>
                <div>
                  <strong>Click "Add custom connector"</strong> at the bottom of the page
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-3 mt-0.5 font-medium">3</span>
                <div>
                  <strong>Add a name</strong> like &quot;Memara – {spaceName}&quot; <strong>and paste the URL</strong> from above, then click &quot;Add&quot;
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-3 mt-0.5 font-medium">4</span>
                <div>
                  <strong>Click "Connect"</strong> and you'll be redirected to Memara for OAuth authentication
                </div>
              </li>
              <li className="flex items-start">
                <span className="bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mr-3 mt-0.5 font-medium">5</span>
                <div>
                  <strong>Select this integration</strong> on the Memara OAuth approval page when prompted
                </div>
              </li>
            </ol>
          </div>

          {/* Test Your Connection */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-medium text-purple-900 mb-3">🧪 Test Your Connection</h4>
            <p className="text-purple-800 text-sm mb-2">
              Once connected, try these prompts in Claude (Desktop or web):
            </p>
            <div className="bg-white border border-purple-200 rounded p-3 space-y-1 text-purple-800 text-sm font-mono">
              <div>• "Search my memories for [topic]"</div>
              <div>• "Remember: I prefer working mornings"</div>
              <div>• "Store this information: [content]"</div>
            </div>
          </div>
        </DialogScrollArea>

        <DialogFooter variant="scrollable" className="border-t border-gray-200 bg-gray-50 px-6 pb-6">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-600 flex items-center">
              <Info className="w-4 h-4 inline mr-1" />
              Secure OAuth 2.1 authentication
            </div>
            <Button
              onClick={onClose}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Done
            </Button>
          </div>
        </DialogFooter>
    </div>
  );
}

interface ClaudeDesktopSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: ClaudeDesktopSuccessIntegration;
  spaceName: string;
}

export function ClaudeDesktopSuccessModal({
  isOpen,
  onClose,
  integration,
  spaceName,
}: ClaudeDesktopSuccessModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent variant="scrollable" className="max-w-2xl">
        <ClaudeDesktopSuccessModalContent
          integration={integration}
          onClose={onClose}
          spaceName={spaceName}
        />
      </DialogContent>
    </Dialog>
  );
}
